import React, { useState, useEffect } from "react";
import { Button } from "primereact/button";
import OptionsList from "./OptionsList";
import DevisSummary from "./DevisSummary";

const getDefaultFormuleName = (formule) =>
  formule === "avec_alcool"
    ? "Formule complète avec alcool"
    : "Formule complète sans alcool";

const getDefaultFormuleDescription = (formule) =>
  formule === "avec_alcool"
    ? "2 cocktails + 1 mocktail par personne, élaborés à partir de produits de qualité.\nPréparation minute, présentation soignée, glaçons, garnitures et consommables inclus."
    : "3 mocktails par personne, élaborés à partir de produits de qualité.\nPréparation minute, présentation soignée, glaçons, garnitures et consommables inclus.";

export default function DevisForm({
  nbPersonnes,
  setNbPersonnes,
  nbBarmen,
  setNbBarmen,
  structure,
  setStructure,
  formule,
  setFormule,
  prixParPersonne,
  setPrixParPersonne,
  remise,
  setRemise,
  options,
  onAddOption,
  onRemoveOption,
  onModifyOption,
  sousTotalPrestation,
  sousTotalOptions,
  montantRemise,
  total,
  generating,
  onGenerate,
  disabled,

  customPackName,
  setCustomPackName,
  customPackDescription,
  setCustomPackDescription,
  customFormuleName,
  setCustomFormuleName,
  customFormuleDescription,
  setCustomFormuleDescription,
}) {
  const [showCustomTexts, setShowCustomTexts] = useState(false);

  const DEFAULT_PACK_NAME =
    "Pack Service & Logistique : 100% Clé en main - 2h à 4h";

  const getPackDescription = (barmenCount) =>
    `- Maîtrise du flux : ${barmenCount} barman(s) expert(s) pour un débit fluide.
- Gestion des consommables : Préparation des garnitures fraîches, gestion de la verrerie et de la glace incluse.
- Logistique : Transport, installation technique et démontage.`;

  // Initialisation par défaut
  useEffect(() => {
    if (!customPackName) setCustomPackName(DEFAULT_PACK_NAME);
    if (!customPackDescription)
      setCustomPackDescription(getPackDescription(nbBarmen));
    if (!customFormuleName)
      setCustomFormuleName(getDefaultFormuleName(formule));
    if (!customFormuleDescription)
      setCustomFormuleDescription(getDefaultFormuleDescription(formule));
  }, []);

  // Recalcul du texte si le nombre de barmen change
  useEffect(() => {
    setCustomPackDescription(getPackDescription(nbBarmen));
  }, [nbBarmen]);

  // Recalcul du titre et texte si la formule dans le select change
  useEffect(() => {
    setCustomFormuleName(getDefaultFormuleName(formule));
    setCustomFormuleDescription(getDefaultFormuleDescription(formule));
  }, [formule]);

  return (
    <>
      <div className="grid mb-2">
        <div className="col-12">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(85px, 1fr))",
              gap: "10px",
              alignItems: "end",
            }}
          >
            <div>
              <label className="field-label">Nb pers</label>
              <input
                type="number"
                className="clean-input"
                value={nbPersonnes}
                onChange={(e) => setNbPersonnes(parseInt(e.target.value) || 0)}
                min={1}
              />
            </div>
            <div>
              <label className="field-label">Barmans</label>
              <input
                type="number"
                className="clean-input"
                value={nbBarmen}
                onChange={(e) => setNbBarmen(parseInt(e.target.value) || 1)}
                min={1}
              />
            </div>
            <div>
              <label className="field-label">Structure</label>
              <select
                className="clean-select"
                value={structure}
                onChange={(e) => setStructure(e.target.value)}
              >
                <option value="tuk_tuk">Tuk-Tuk</option>
                <option value="elegance">Élégance</option>
                <option value="both">Les deux</option>
              </select>
            </div>
            <div>
              <label className="field-label">Formule</label>
              <select
                className="clean-select"
                value={formule}
                onChange={(e) => setFormule(e.target.value)}
              >
                <option value="avec_alcool">Avec alcool</option>
                <option value="sans_alcool">Sans alcool</option>
              </select>
            </div>
            <div>
              <label className="field-label">Prix/p (€)</label>
              <input
                type="number"
                className="clean-input"
                value={prixParPersonne}
                onChange={(e) =>
                  setPrixParPersonne(parseFloat(e.target.value) || 0)
                }
                min={0}
              />
            </div>
            <div>
              <label className="field-label">Remise (%)</label>
              <input
                type="number"
                className="clean-input"
                value={remise}
                onChange={(e) => setRemise(parseFloat(e.target.value) || 0)}
                min={0}
                max={100}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="grid mb-3">
        <div className="col-12">
          <div
            style={{
              borderRadius: "8px",
              overflow: "hidden",
            }}
          >
            <button
              onClick={() => setShowCustomTexts(!showCustomTexts)}
              style={{
                background: "none",
                border: "1px dashed #d1d5db",
                borderRadius: "6px",
                width: "100%",
                padding: "8px",
                marginTop: "5px",
                marginBottom: "5px",
                fontSize: "13px",
                color: "#322915",
                cursor: "pointer",
              }}
            >
              Personnaliser les textes du devis
            </button>

            {showCustomTexts && (
              <div>
                {/* Pack Service & Logistique */}
                <div style={{ marginBottom: "8px" }}>
                  <span className="field-label">Pack Service & Logistique</span>
                  <input
                    className="clean-input"
                    value={customPackName}
                    onChange={(e) => setCustomPackName(e.target.value)}
                    placeholder="Titre du pack"
                    style={{ fontSize: "13px", marginBottom: "6px" }}
                  />
                  <textarea
                    className="clean-input"
                    value={customPackDescription}
                    onChange={(e) => setCustomPackDescription(e.target.value)}
                    placeholder="Description du pack"
                    rows={6}
                    style={{
                      fontSize: "13px",
                      resize: "vertical",
                      height: "84px",
                    }}
                  />
                </div>

                {/* Formule */}
                <div>
                  <span className="field-label">
                    Formule{" "}
                    {formule === "avec_alcool" ? "avec alcool" : "sans alcool"}
                  </span>
                  <input
                    className="clean-input"
                    value={customFormuleName}
                    onChange={(e) => setCustomFormuleName(e.target.value)}
                    placeholder="Titre de la formule"
                    style={{ fontSize: "13px", marginBottom: "6px" }}
                  />
                  <textarea
                    className="clean-input"
                    value={customFormuleDescription}
                    onChange={(e) =>
                      setCustomFormuleDescription(e.target.value)
                    }
                    placeholder="Description de la formule"
                    rows={6}
                    style={{
                      fontSize: "13px",
                      resize: "vertical",
                      height: "84px",
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <OptionsList
        options={options}
        onAdd={onAddOption}
        onRemove={onRemoveOption}
        onModify={onModifyOption}
      />

      <DevisSummary
        nbPersonnes={nbPersonnes}
        sousTotalPrestation={sousTotalPrestation}
        options={options}
        remise={remise}
        montantRemise={montantRemise}
        total={total}
      />

      <div className="grid mt-3">
        <div className="col-12">
          <Button
            label={generating ? "Génération en cours..." : "Créer le devis"}
            className="w-full brand-btn-primary"
            style={{
              padding: "0.7rem",
              fontSize: "13.5px",
              justifyContent: "center",
            }}
            onClick={onGenerate}
            disabled={disabled}
          />
        </div>
      </div>
    </>
  );
}